/** @jsxImportSource @emotion/react */
import { jsx, css } from '@emotion/react';
import { FC, useState, createContext, FormEvent, useEffect } from 'react';
import { gray5, gray6 } from './style';
import Button from "./Button";


export interface Values {
    [key: string]: any;
}


export interface Errors {
    [key: string]: string[];
}


export interface Touched {
    [key: string]: boolean;
}


interface FormContextProps {
    values: Values;
    setValue?: (fileName: string, value: any) => void;

    // Lets add the validation errors and whether a 
    // field has been touched to the form context
    errors: Errors;
    validate?: (fieldName: string) => void;
    touched: Touched;
    setTouched?: (fieldName: string) => void;
}

export const FormContext = createContext<FormContextProps>({
    values: {},
    errors: {},
    touched: {},
});


type Validator = (value: any, args?: any) => string;

interface Validation {
    validator: Validator;
    arg?: any;
}
interface ValidationProp {
    [key: string]: Validation | Validation[];
}

export interface SubmitResult {
    success: boolean;
    errors?: Errors;
}

export const required: Validator = (value: any): string => value === undefined || value === null || value === '' ? 'This must be populated' : '';


export const minLength: Validator = (value: any, length: number,): string => value && value.length < length ? `This must be a least ${length} characters` : '';

let regexEmail = new RegExp('[a-z0-9]+@[a-z]+\\.[a-z]{2,3}');
export const emailValidator: Validator = (value: any): string => {
    return !regexEmail.test(value) ? "Please provide a valid email" : '';
}


interface Props {
    submitCaption?: string;
    validationRules?: ValidationProp;


    onSubmit: (values: Values) => Promise<SubmitResult>;

    successMessage?: string;
    failureMessage?: string;

    children: React.ReactNode;
}

export const Form: FC<Props> = ({ submitCaption,
    children,
    validationRules,
    onSubmit,
    successMessage = 'Success!',
    failureMessage = 'Something went wrong' }) => {
    const [values, setValues] = useState<Values>({});
    const [errors, setErrors] = useState<Errors>({});
    const [touched, setTouched] = useState<Touched>({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [submitMessage, setSubmitMessage] = useState({});

    const validate = (fieldName: string): string[] => {
        if (!validationRules) {
            return [];
        }
        if (!validationRules[fieldName]) {
            return [];
        }


        const rules = Array.isArray(validationRules[fieldName]) ? (validationRules[fieldName] as Validation[]) : ([validationRules[fieldName]] as Validation[])

        const fieldErrors: string[] = [];
        rules.forEach(rule => {
            const error = rule.validator(values[fieldName], rule.arg);
            if (error) {
                fieldErrors.push(error);
            }
        });

        const newErrors = { ...errors, [fieldName]: fieldErrors };
        setErrors(newErrors);
        return fieldErrors;
    }



    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (validateForm()) {
            setSubmitting(true);
            setSubmitError(false);
            const result = await onSubmit(values);
            setErrors(result.errors || {});
            setSubmitError(!result.success);
            setSubmitMessage(result["submitMessage"])
            setSubmitting(false);
            setSubmitted(true);
            setValues({
                contactName: "",
                contactEmail: "",
                contactMessage: ""
            });

        }

    };

    const validateForm = () => {
        const newErrors: Errors = {};
        let haveError: boolean = false;
        if (validationRules) {
            Object.keys(validationRules).forEach(fieldName => {
                newErrors[fieldName] = validate(fieldName);
                if (newErrors[fieldName].length > 0) {
                    haveError = true;
                }
            });
        }
        setErrors(newErrors);
        return !haveError;
    };
    return (
        <FormContext.Provider
            value={{
                values, setValue: (fieldName: string, value: any) => {
                    setValues({ ...values, [fieldName]: value });
                },
                errors,
                validate,
                touched,
                setTouched: (fieldName: string) => {
                    setTouched({ ...touched, [fieldName]: true });
                }
            }}
        >
            <form noValidate={true} onSubmit={handleSubmit}>
                <fieldset disabled={submitting || (submitted && !submitError)} css={css`
                        margin: 10px auto 0 auto;
                        padding: 30px;
                        width: 100%;
                        background-color: ${gray6};
                        border-radius: 4px;
                        border: 1px solid ${gray5};
                        box-shadow: 0 3px 5px 0 rgba(0,0,0.16);
                    `}>
                    {children}
                    <div css={css`
                            margin: 30px 0px 0px 0px;
                            padding: 20px 0px 0px 0px;
                            border-top: 1px solid ${gray5};
                        `}>
                        <Button className='button button--outline button--fit ' type="submit">
                            {submitCaption}
                        </Button>
                    </div>
                    {submitted && submitError && (
                        <p className="validation--error"
                        // css={css`
                        //     color: red;
                        // `}
                        >
                            {submitMessage}
                        </p>
                    )}
                    {submitted && !submitError && (
                        <p className="validation--success"
                        // css={css`
                        //     color: green;
                        // `}
                        >
                            {submitMessage}
                        </p>
                    )}
                </fieldset>
            </form>
        </FormContext.Provider>
    )
}